import type { QueryClient } from "@tanstack/react-query";
import type { AppDispatch } from "../redux/store";
import { clearAuth } from "../redux/authSlice";
import { logout as clearCompany } from "../redux/companySlice";
import API from "./api";
import authService from "./authService";
import companyService from "./companyService";

export type SessionPrincipalType = "user" | "company" | null;

const LOCAL_AUTH_KEYS = [
  "accessToken",
  "refreshToken",
  "principalType",
  "roleType",
  "permissions",
  "tokenExpiresIn",
] as const;

const SESSION_AUTH_KEYS = ["session_expired"] as const;

export function clearAuthStorage(): void {
  if (typeof localStorage !== "undefined") {
    LOCAL_AUTH_KEYS.forEach((key) => localStorage.removeItem(key));
  }
  if (typeof sessionStorage !== "undefined") {
    SESSION_AUTH_KEYS.forEach((key) => sessionStorage.removeItem(key));
  }
}

export function clearFrontendSession(
  dispatch: AppDispatch,
  queryClient?: QueryClient,
): void {
  API.clearRefreshTimer();
  clearAuthStorage();
  dispatch(clearAuth());
  dispatch(clearCompany());
  queryClient?.clear();
}

export async function logoutStorefrontSession({
  principalType,
  dispatch,
  queryClient,
}: {
  principalType: SessionPrincipalType;
  dispatch: AppDispatch;
  queryClient?: QueryClient;
}): Promise<void> {
  const accessToken =
    typeof localStorage === "undefined"
      ? null
      : localStorage.getItem("accessToken");
  let logoutRequest: Promise<unknown> | null = null;
  try {
    logoutRequest =
      principalType === "company"
        ? companyService.logout(accessToken)
        : principalType === "user"
          ? authService.logout(accessToken)
          : null;
  } catch {
    // Cleanup below must still complete if request creation fails.
  } finally {
    clearFrontendSession(dispatch, queryClient);
  }

  try {
    await logoutRequest;
  } catch {
    // Local logout is authoritative even when the server is unavailable.
  }
}
